import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import PageLoader from '../components/PageLoader';

const ForwardingConfig = () => {
  const { t } = useTranslation();
  const [workflows, setWorkflows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Workflow dialog
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [workflowForm, setWorkflowForm] = useState({ name: '', description: '', is_active: true });
  const [workflowSaving, setWorkflowSaving] = useState(false);

  // Step dialog
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [stepWorkflow, setStepWorkflow] = useState(null);
  const [stepForm, setStepForm] = useState({ from_role: '', to_role: '', step_order: 1, set_status: '' });
  const [stepSaving, setStepSaving] = useState(false);

  // Delete confirmations
  const [deleteWorkflowId, setDeleteWorkflowId] = useState(null);
  const [deleteStepInfo, setDeleteStepInfo] = useState(null); // { workflowId, stepId }

  const fetchWorkflows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/forwarding-workflows/');
      setWorkflows(res.data.results || res.data);
      setError('');
    } catch (err) {
      setError(t('forwardingConfig.errorLoadFailed'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await apiClient.get('/roles/');
      setRoles(res.data.results || res.data);
    } catch (err) {
      console.error('Failed to load roles', err);
    }
  }, []);

  useEffect(() => {
    fetchWorkflows();
    fetchRoles();
  }, [fetchWorkflows, fetchRoles]);

  // --- Workflow CRUD ---

  const openCreateWorkflow = () => {
    setEditingWorkflow(null);
    setWorkflowForm({ name: '', description: '', is_active: true });
    setWorkflowDialogOpen(true);
  };

  const openEditWorkflow = (wf) => {
    setEditingWorkflow(wf);
    setWorkflowForm({ name: wf.name, description: wf.description || '', is_active: wf.is_active });
    setWorkflowDialogOpen(true);
  };

  const saveWorkflow = async () => {
    if (!workflowForm.name.trim()) return;
    setWorkflowSaving(true);
    try {
      if (editingWorkflow) {
        await apiClient.patch(`/forwarding-workflows/${editingWorkflow.id}/`, workflowForm);
        setSuccess(t('forwardingConfig.successWorkflowUpdated'));
      } else {
        await apiClient.post('/forwarding-workflows/', workflowForm);
        setSuccess(t('forwardingConfig.successWorkflowCreated'));
      }
      setWorkflowDialogOpen(false);
      fetchWorkflows();
    } catch (err) {
      setError(err.response?.data?.error || t('forwardingConfig.errorSaveWorkflow'));
    } finally {
      setWorkflowSaving(false);
    }
  };

  const confirmDeleteWorkflow = async () => {
    try {
      await apiClient.delete(`/forwarding-workflows/${deleteWorkflowId}/`);
      setSuccess(t('forwardingConfig.successWorkflowDeleted'));
      setDeleteWorkflowId(null);
      fetchWorkflows();
    } catch (err) {
      setError(t('forwardingConfig.errorDeleteWorkflow'));
    }
  };

  // --- Step CRUD ---

  const openAddStep = (workflow) => {
    setStepWorkflow(workflow);
    // Suggest next step order
    const maxOrder = workflow.steps.length > 0
      ? Math.max(...workflow.steps.map(s => s.step_order))
      : 0;
    setStepForm({ from_role: '', to_role: '', step_order: maxOrder + 1, set_status: '' });
    setStepDialogOpen(true);
  };

  const saveStep = async () => {
    if (!stepForm.from_role || !stepForm.to_role) return;
    setStepSaving(true);
    try {
      await apiClient.post(`/forwarding-workflows/${stepWorkflow.id}/add_step/`, stepForm);
      setSuccess(t('forwardingConfig.successStepAdded'));
      setStepDialogOpen(false);
      fetchWorkflows();
      fetchRoles();
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.error || errData?.non_field_errors?.[0] || t('forwardingConfig.errorAddStep');
      setError(msg);
    } finally {
      setStepSaving(false);
    }
  };

  const confirmDeleteStep = async () => {
    const { workflowId, stepId } = deleteStepInfo;
    try {
      await apiClient.delete(`/forwarding-workflows/${workflowId}/remove_step/${stepId}/`);
      setSuccess(t('forwardingConfig.successStepRemoved'));
      setDeleteStepInfo(null);
      fetchWorkflows();
    } catch (err) {
      setError(t('forwardingConfig.errorRemoveStep'));
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? (role.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())) : `Role #${roleId}`;
  };

  if (loading) {
    return <PageLoader variant="cards" cards={3} />;
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">{t('forwardingConfig.pageTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('forwardingConfig.pageSubtitle')}
          </Typography>
        </Box>
        <Tooltip title={workflows.length > 0 ? t('forwardingConfig.workflowExistsTooltip') : ''}>
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateWorkflow}
              disabled={workflows.length > 0}
            >
              {t('forwardingConfig.newWorkflow')}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
      )}

      {workflows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('forwardingConfig.noWorkflows')}</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={openCreateWorkflow}>
            {t('forwardingConfig.createFirstWorkflow')}
          </Button>
        </Paper>
      ) : (
        workflows.map(wf => (
          <Card key={wf.id} sx={{ mb: 3, border: wf.is_active ? '1px solid #4caf50' : '1px solid #e0e0e0' }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6" fontWeight="bold">{wf.name}</Typography>
                  <Chip
                    size="small"
                    icon={wf.is_active ? <ActiveIcon /> : <InactiveIcon />}
                    label={wf.is_active ? t('common.active') : t('feeManagement.inactiveLabel')}
                    color={wf.is_active ? 'success' : 'default'}
                    variant="outlined"
                  />
                </Box>
              }
              subheader={wf.description || t('forwardingConfig.noDescription')}
              action={
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  <Tooltip title={t('forwardingConfig.addStepTooltip')}>
                    <IconButton size="small" color="primary" onClick={() => openAddStep(wf)}>
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('forwardingConfig.editWorkflowTooltip')}>
                    <IconButton size="small" onClick={() => openEditWorkflow(wf)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('forwardingConfig.deleteWorkflowTooltip')}>
                    <IconButton size="small" color="error" onClick={() => setDeleteWorkflowId(wf.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
            <Divider />
            <CardContent>
              {wf.steps.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  {t('forwardingConfig.noStepsYet')}
                </Typography>
              ) : (
                <>
                  {/* Visual chain */}
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {[...wf.steps].sort((a, b) => a.step_order - b.step_order).map((step, idx) => (
                      <React.Fragment key={step.id}>
                        {idx === 0 && (
                          <Chip
                            label={step.from_role_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            color="primary"
                            variant="filled"
                            size="small"
                          />
                        )}
                        <ArrowForwardIcon fontSize="small" color="action" />
                        <Chip
                          label={step.to_role_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          color="success"
                          variant="filled"
                          size="small"
                        />
                      </React.Fragment>
                    ))}
                  </Box>

                  {/* Steps table */}
                  <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell width={60}><strong>{t('forwardingConfig.orderCol')}</strong></TableCell>
                          <TableCell><strong>{t('forwardingConfig.fromRoleCol')}</strong></TableCell>
                          <TableCell width={40}></TableCell>
                          <TableCell><strong>{t('forwardingConfig.toRoleCol')}</strong></TableCell>
                          <TableCell><strong>{t('forwardingConfig.setsStatusCol')}</strong></TableCell>
                          <TableCell width={60} align="right"><strong>{t('permitList.actions')}</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...wf.steps].sort((a, b) => a.step_order - b.step_order).map(step => (
                          <TableRow key={step.id} hover>
                            <TableCell>{step.step_order}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={step.from_role_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <ArrowForwardIcon fontSize="small" color="action" />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={step.to_role_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                color="success"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {step.set_status ? (
                                <Chip
                                  size="small"
                                  label={t(`common.${step.set_status}`, { defaultValue: step.set_status.charAt(0).toUpperCase() + step.set_status.slice(1) })}
                                  color={step.set_status === 'active' ? 'success' : step.set_status === 'cancelled' ? 'error' : 'default'}
                                  variant="filled"
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={t('forwardingConfig.removeStepTooltip')}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setDeleteStepInfo({ workflowId: wf.id, stepId: step.id })}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {/* Create / Edit Workflow Dialog */}
      <Dialog open={workflowDialogOpen} onClose={() => setWorkflowDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingWorkflow ? t('forwardingConfig.editWorkflowTitle') : t('forwardingConfig.newWorkflowTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label={t('forwardingConfig.workflowNameLabel')}
            fullWidth
            margin="normal"
            value={workflowForm.name}
            onChange={e => setWorkflowForm(f => ({ ...f, name: e.target.value }))}
            placeholder={t('forwardingConfig.workflowNamePlaceholder')}
          />
          <TextField
            label={t('permitSearch.description')}
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={workflowForm.description}
            onChange={e => setWorkflowForm(f => ({ ...f, description: e.target.value }))}
            placeholder={t('forwardingConfig.descriptionPlaceholder')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={workflowForm.is_active}
                onChange={e => setWorkflowForm(f => ({ ...f, is_active: e.target.checked }))}
              />
            }
            label={t('forwardingConfig.activeSwitchLabel')}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkflowDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={saveWorkflow}
            disabled={workflowSaving || !workflowForm.name.trim()}
          >
            {workflowSaving ? <CircularProgress size={20} /> : (editingWorkflow ? t('common.save') : t('feeManagement.create'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Step Dialog */}
      <Dialog open={stepDialogOpen} onClose={() => setStepDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('forwardingConfig.addStepTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('forwardingConfig.addStepSubtitle')}
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('forwardingConfig.fromRoleCol')}</InputLabel>
            <Select
              value={stepForm.from_role}
              label={t('forwardingConfig.fromRoleCol')}
              onChange={e => setStepForm(f => ({ ...f, from_role: e.target.value }))}
            >
              {roles.map(r => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 1 }}>
            <ArrowForwardIcon color="action" />
          </Box>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('forwardingConfig.toRoleCol')}</InputLabel>
            <Select
              value={stepForm.to_role}
              label={t('forwardingConfig.toRoleCol')}
              onChange={e => setStepForm(f => ({ ...f, to_role: e.target.value }))}
            >
              {roles.map(r => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('forwardingConfig.stepOrderLabel')}
            type="number"
            fullWidth
            margin="normal"
            inputProps={{ min: 1 }}
            value={stepForm.step_order}
            onChange={e => setStepForm(f => ({ ...f, step_order: parseInt(e.target.value, 10) || 1 }))}
            helperText={t('forwardingConfig.stepOrderHelper')}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('forwardingConfig.changeStatusLabel')}</InputLabel>
            <Select
              value={stepForm.set_status}
              label={t('forwardingConfig.changeStatusLabel')}
              onChange={e => setStepForm(f => ({ ...f, set_status: e.target.value }))}
            >
              <MenuItem value=""><em>{t('forwardingConfig.noChangeOption')}</em></MenuItem>
              <MenuItem value="active">{t('forwardingConfig.activeApprovedOption')}</MenuItem>
              <MenuItem value="pending">{t('forwardingConfig.pendingOption')}</MenuItem>
              <MenuItem value="inactive">{t('forwardingConfig.inactiveOption')}</MenuItem>
              <MenuItem value="cancelled">{t('forwardingConfig.cancelledRejectedOption')}</MenuItem>
              <MenuItem value="expired">{t('forwardingConfig.expiredOption')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStepDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={saveStep}
            disabled={stepSaving || !stepForm.from_role || !stepForm.to_role}
          >
            {stepSaving ? <CircularProgress size={20} /> : t('forwardingConfig.addStep')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Workflow Confirmation */}
      <Dialog open={!!deleteWorkflowId} onClose={() => setDeleteWorkflowId(null)}>
        <DialogTitle>{t('forwardingConfig.deleteWorkflowTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('forwardingConfig.deleteWorkflowConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteWorkflowId(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteWorkflow}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Step Confirmation */}
      <Dialog open={!!deleteStepInfo} onClose={() => setDeleteStepInfo(null)}>
        <DialogTitle>{t('forwardingConfig.removeStepTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('forwardingConfig.removeStepConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteStepInfo(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteStep}>{t('forwardingConfig.remove')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ForwardingConfig;
